import { useCallback, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Screen } from '../components/Screen'
import { useEnv } from '../context'
import { useClubContext, useHubContextLink } from '../data/hooks'
import type { HubContextRef } from '../data/types'
import type { HubContext } from '../platform/platform'

/** «контекст тренировки», «контекст группы», «контекст спортсмена» */
const ABOUT: Record<HubContextRef['kind'], string> = {
  session: 'тренировки',
  group: 'группы',
  athlete: 'спортсмена',
}

/**
 * S16 «Обсудить в боте» (мини-приложения Telegram и MAX). Показывает, что
 * уйдёт в бота, и открывает его по ссылке с одноразовым кодом: ни имён, ни ID
 * в ссылке нет, бот принимает код только от этого же пользователя и ничего не
 * выполняет без запроса. Код запрашивается при открытии экрана, а не по
 * нажатию: MAX открывает ссылку только прямо из обработчика нажатия.
 *
 * Запасной путь: код не получен, устарел или переход недоступен - понятное
 * сообщение и обычный переход в бота без контекста.
 */
export function HubBotScreen() {
  const { platform, toast } = useEnv()
  const navigate = useNavigate()
  const location = useLocation()
  const club = useClubContext()
  const context = (location.state as { context?: HubContext } | null)?.context
  const ref = context?.ref
  const plainUrl = platform.botLink()
  const { state, retry, isFresh } = useHubContextLink(plainUrl ? platform.botChannel : null, ref)
  const [opened, setOpened] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [blocked, setBlocked] = useState<string | null>(null)

  const back = useCallback(() => {
    if (location.key !== 'default') navigate(-1)
    else navigate('/today', { replace: true })
  }, [location.key, navigate])

  const open = (url: string | null) => {
    if (!url || !platform.openBotLink(url)) {
      setBlocked(url ?? plainUrl)
      return false
    }
    return true
  }

  const goWithContext = () => {
    if (state.status !== 'ready') return
    if (!isFresh(state.link)) {
      // Пока экран был открыт, срок кода вышел: готовим новый, а не ведём в
      // бота с ссылкой, которую он отклонит
      setNotice('Переход устарел, готовлю новый. Нажмите «Перейти в бота» ещё раз.')
      retry()
      return
    }
    setNotice(null)
    if (open(platform.botLink(state.link.startParam))) {
      platform.haptic('success')
      setOpened(true)
      toast('Открываю бота. Ответ придёт туда же, где вы спросили.')
    }
  }

  const goPlain = () => {
    if (open(plainUrl)) toast('Открываю бота без контекста.')
  }

  // Переход в бота невозможен: имени бота нет или среда не мини-приложение
  if (!plainUrl) {
    return (
      <Screen title="Обсудить в боте" back={back} hideHub>
        <div className="pc-state" role="alert">
          <h3>Переход в бота недоступен</h3>
          <p>Отсюда бота Штаба не открыть. Найдите бота ProCoach в мессенджере и напишите вопрос там.</p>
        </div>
      </Screen>
    )
  }

  // Экран открыт без контекста (по ссылке или после перезагрузки)
  if (!context || !ref) {
    return (
      <Screen
        title="Обсудить в боте"
        back={back}
        hideHub
        actionBar={
          <button type="button" className="pc-btn pc-btn--primary pc-btn--block" onClick={goPlain}>
            Перейти в бота
          </button>
        }
      >
        <p className="pc-note">Контекст экрана не передан. Бот откроется без него - вопрос можно описать самому.</p>
        {blocked && <BlockedLink url={blocked} />}
      </Screen>
    )
  }

  const ready = state.status === 'ready'
  const actionBar = (
    <>
      {opened ? (
        <button type="button" className="pc-btn pc-btn--primary pc-btn--block" onClick={goPlain}>
          Открыть бота снова
        </button>
      ) : (
        <button
          type="button"
          className="pc-btn pc-btn--primary pc-btn--block"
          disabled={!ready}
          aria-disabled={!ready}
          onClick={goWithContext}
        >
          Перейти в бота
        </button>
      )}
      <button type="button" className="pc-btn pc-btn--block" onClick={back}>
        Остаться здесь
      </button>
    </>
  )

  return (
    <Screen title="Обсудить в боте" back={back} hideHub actionBar={actionBar}>
      <section className="pc-section pc-card" aria-labelledby="pc-hb-title">
        <p className="pc-muted" style={{ margin: '0 0 4px' }}>
          Контекст для Штаба
        </p>
        <h2 className="pc-section-title" id="pc-hb-title">
          {context.title ?? context.label}
        </h2>
        {context.detail && <p style={{ margin: 0 }}>{context.detail}</p>}
        {club.data && (
          <p className="pc-muted" style={{ margin: '8px 0 0' }}>
            {`${club.data.clubName} / ${club.data.directionName}`}
          </p>
        )}
      </section>

      <p className="pc-section">
        Откроется чат бота. Он получит контекст {ABOUT[ref.kind]}, но не выполнит действие без вашего запроса.
      </p>

      <div role="status" aria-live="polite">
        {state.status === 'loading' && <p className="pc-muted">Готовлю переход...</p>}
        {opened && <p className="pc-note">Бот открыт. Напишите вопрос там - бот добавит к нему этот контекст.</p>}
        {notice && <p className="pc-note">{notice}</p>}
      </div>

      {state.status === 'error' && (
        <div className="pc-state" data-tone="error" role="alert">
          <h3>Контекст не передан</h3>
          <p>
            {state.error.kind === 'not_found'
              ? 'Этот объект недоступен: возможно, его удалили или доступ закрыт.'
              : 'Не удалось подготовить переход: нет связи с сервером.'}{' '}
            Можно открыть бота без контекста и описать вопрос самому.
          </p>
          {state.error.kind !== 'not_found' && (
            <button type="button" className="pc-btn pc-btn--primary" onClick={retry}>
              Повторить
            </button>
          )}
          <button type="button" className="pc-btn" onClick={goPlain} style={{ marginTop: 8 }}>
            Открыть бота без контекста
          </button>
        </div>
      )}

      {blocked && <BlockedLink url={blocked} />}
    </Screen>
  )
}

/** Мессенджер не открыл ссылку: показываем её, чтобы перейти вручную. */
function BlockedLink({ url }: { url: string }) {
  return (
    <div className="pc-alert" role="alert" style={{ display: 'block' }}>
      <p style={{ margin: 0 }}>Не получилось открыть бота отсюда. Откройте ссылку вручную:</p>
      <a href={url} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>
        {url}
      </a>
    </div>
  )
}
